/**
 * Reglas del baseline.
 *
 * El baseline es la medición que decide el peso de todo el programa. Si se da
 * por hecho sin haberlo hecho de verdad, las doce semanas se entrenan contra
 * datos inventados.
 *
 * De ahí la regla dura: **un baseline solo cuenta si está COMPLETADO**. Uno
 * parcial no habilita el Día 1; ofrece rehacerlo.
 */

import type { Grabacion } from '../modelo/grabacion.js';
import { cuentaComoHecha, type SesionRegistrada } from '../modelo/sesion.js';

/**
 * ¿Este paso EXIGE una muestra de audio para darse por hecho?
 *
 * Solo si la herramienta del paso ES la grabadora. Grabar es entonces el
 * ejercicio, no un accesorio: el baseline, los checkpoints y el
 * micro-shadowing del Día 2 no significan nada sin la muestra.
 *
 * `grabadora: true` con OTRA herramienta significa «acá también podés
 * grabarte», y eso no puede bloquear nada. En el Día 5 el paso ofrece la
 * grabadora, pero exigirla cuando el foco es listening o gramática sería
 * pedir una muestra que el ejercicio no usa.
 */
export function pasoRequiereGrabacion(paso: {
  herramienta: string;
  grabadora?: boolean;
}): boolean {
  return paso.herramienta === 'grabadora';
}

/** ¿El paso OFRECE grabar, aunque no lo exija? */
export function pasoOfreceGrabacion(paso: {
  herramienta: string;
  grabadora?: boolean;
}): boolean {
  return paso.herramienta === 'grabadora' || paso.grabadora === true;
}

/** ¿La sesión ya tiene la muestra de este paso? */
export function tieneGrabacionDePaso(
  grabaciones: Grabacion[],
  sesionId: string,
  stepKey: string,
): boolean {
  return grabaciones.some(
    (g) => !g.deleted && g.sesionId === sesionId && g.stepKey === stepKey,
  );
}

/**
 * ¿Este baseline habilita el programa?
 *
 * Solo si está `completada`. Un baseline `parcial` significa que algo se
 * omitió —típicamente la muestra de audio—, y arrancar el Día 1 sobre eso
 * sería exactamente el «completado artificialmente» que la especificación
 * prohíbe.
 */
export function baselineEsValido(sesion: SesionRegistrada): boolean {
  return sesion.tipo === 'baseline' && !sesion.deleted && sesion.estado === 'completada';
}

/**
 * Un baseline empezado que quedó a medias y no habilita nada.
 *
 * La interfaz lo usa para ofrecer rehacerlo en vez de dejar al usuario
 * pensando que ya lo tiene hecho.
 */
export function baselineIncompleto(sesiones: SesionRegistrada[]): SesionRegistrada | null {
  if (sesiones.some(baselineEsValido)) return null;
  const intentos = sesiones.filter(
    (s) => s.tipo === 'baseline' && !s.deleted && cuentaComoHecha(s),
  );
  if (intentos.length === 0) return null;
  return intentos.reduce((a, b) => (a.iniciadaEn >= b.iniciadaEn ? a : b));
}

// ── Dificultades principales ───────────────────────────────────────────────

/**
 * Terminología obligatoria: «dificultad principal», nunca «cuello de botella».
 */
export const QUE_SON_LAS_DIFICULTADES =
  'Son las cosas que realmente te frenaron durante la evaluación: por ejemplo, no entender habla rápida, tardar buscando palabras, quedarte en blanco, no poder reformular, repetir demasiado «uh/um» o notar un problema de pronunciación.';

/** Cuántas se pueden registrar. Ni cero obligatorias, ni una lista infinita. */
export const MAXIMO_DIFICULTADES = 5;

export const AVISO_NO_INVENTAR =
  'Guardá solo las que notaste de verdad. Si fue una, guardá una: un banco con problemas inventados hace que el programa te recomiende entrenar cosas que no te pasan.';
